import React, { useEffect, useState } from "react";
import { RefreshCcw, Tag, Plus, Trash2, Pencil, X, Users, TicketPercent, ChevronDown, ChevronUp } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
    code: "", description: "", source: "",
    discount_type: "percent", discount_value: 10,
    max_uses: 0, valid_from: "", valid_until: "", active: true,
};

const fmtSAR = (n) => `${Number(n || 0).toLocaleString("tr-TR")} SAR`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return iso || "—"; } };

const STATUS_TR = { pending: "Beklemede", approved: "Onaylı", rejected: "Reddedildi" };

const AdminPromoCodes = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null); // id being edited
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/promo-stats");
            setItems(data);
        } catch { toast.error("Kodlar yüklenemedi"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
    const openEdit = (p) => {
        setEditing(p.id);
        setForm({
            code: p.code, description: p.description || "", source: p.source || "",
            discount_type: p.discount_type, discount_value: p.discount_value,
            max_uses: p.max_uses || 0, valid_from: p.valid_from || "", valid_until: p.valid_until || "",
            active: p.active !== false,
        });
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.code.trim()) { toast.error("Kod zorunlu"); return; }
        setSaving(true);
        const payload = {
            ...form,
            discount_value: Number(form.discount_value) || 0,
            max_uses: Number(form.max_uses) || 0,
            valid_from: form.valid_from || null,
            valid_until: form.valid_until || null,
        };
        try {
            if (editing) await api.put(`/admin/promo-codes/${editing}`, payload);
            else await api.post("/admin/promo-codes", payload);
            toast.success(editing ? "Kod güncellendi" : "Kod oluşturuldu");
            setShowForm(false);
            setForm(EMPTY);
            setEditing(null);
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Kaydedilemedi"); }
        finally { setSaving(false); }
    };

    const toggleActive = async (p) => {
        try {
            await api.put(`/admin/promo-codes/${p.id}`, { active: !p.active });
            setItems((cur) => cur.map((x) => (x.id === p.id ? { ...x, active: !p.active } : x)));
            toast.success(p.active ? "Kod pasife alındı" : "Kod aktifleştirildi");
        } catch { toast.error("Güncellenemedi"); }
    };

    const remove = async (p) => {
        if (!window.confirm(`"${p.code}" kodu silinsin mi?`)) return;
        try {
            await api.delete(`/admin/promo-codes/${p.id}`);
            setItems((cur) => cur.filter((x) => x.id !== p.id));
            toast.success("Silindi");
        } catch { toast.error("Silinemedi"); }
    };

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">İndirim Kodları</h1>
                    <p className="text-muted_green text-sm">Kod oluşturun, indirim oranını belirleyin ve hangi koddan hangi müşterilerin geldiğini takip edin.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="btn-outline-gold !text-xs" data-testid="refresh-promos"><RefreshCcw className="w-3.5 h-3.5" /> Yenile</button>
                    <button onClick={openCreate} className="btn-gold !text-xs" data-testid="new-promo-btn"><Plus className="w-3.5 h-3.5" /> Yeni Kod</button>
                </div>
            </div>

            {showForm && (
                <form onSubmit={save} className="border border-gold/40 bg-emerald_surface p-6 mb-8 space-y-4" data-testid="promo-form">
                    <div className="flex items-center justify-between">
                        <h2 className="font-serif text-2xl">{editing ? "Kodu Düzenle" : "Yeni İndirim Kodu"}</h2>
                        <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="text-muted_green hover:text-gold"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="label-dark">Kod *</label>
                            <input type="text" className="input-dark uppercase font-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="ÖRN: AHMET10" data-testid="promo-code-input" />
                        </div>
                        <div>
                            <label className="label-dark">İndirim Tipi</label>
                            <select className="input-dark" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="promo-type-select">
                                <option value="percent">Yüzde (%)</option>
                                <option value="fixed">Sabit Tutar (SAR)</option>
                            </select>
                        </div>
                        <div>
                            <label className="label-dark">İndirim Değeri {form.discount_type === "percent" ? "(%)" : "(SAR)"}</label>
                            <input type="number" min={0} step="0.01" className="input-dark" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="promo-value-input" />
                        </div>
                        <div>
                            <label className="label-dark">Kaynak / Referans (kim dağıtıyor?)</label>
                            <input type="text" className="input-dark" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Örn: Ahmet Hoca, Instagram, X Acentesi" data-testid="promo-source-input" />
                        </div>
                        <div>
                            <label className="label-dark">Açıklama</label>
                            <input type="text" className="input-dark" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Not / kampanya açıklaması" />
                        </div>
                        <div>
                            <label className="label-dark">Maks. Kullanım (0 = sınırsız)</label>
                            <input type="number" min={0} className="input-dark" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} />
                        </div>
                        <div>
                            <label className="label-dark">Geçerlilik Başlangıcı</label>
                            <input type="date" className="input-dark" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
                        </div>
                        <div>
                            <label className="label-dark">Geçerlilik Bitişi</label>
                            <input type="date" className="input-dark" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
                        </div>
                        <div className="flex items-end pb-2">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#d4af37]" /> Aktif
                            </label>
                        </div>
                    </div>
                    <button type="submit" disabled={saving} className="btn-gold !text-sm" data-testid="save-promo-btn">
                        {saving ? "Kaydediliyor…" : editing ? "Güncelle" : "Kodu Oluştur"}
                    </button>
                </form>
            )}

            {loading ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-border_green p-12 text-center">
                    <TicketPercent className="w-14 h-14 text-muted_green mx-auto mb-3" />
                    <div className="text-muted_green text-sm">Henüz indirim kodu yok. "Yeni Kod" ile oluşturun.</div>
                </div>
            ) : (
                <div className="space-y-4">
                    {items.map((p) => (
                        <div key={p.id} className="border border-border_green bg-emerald_surface" data-testid={`promo-${p.code}`}>
                            <div className="p-5">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <span className="font-mono text-xl text-gold flex items-center gap-1.5"><Tag className="w-4 h-4" /> {p.code}</span>
                                            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border ${p.active !== false ? "text-emerald-300 border-emerald-500/60 bg-emerald-900/30" : "text-muted_green border-border_green"}`}>
                                                {p.active !== false ? "Aktif" : "Pasif"}
                                            </span>
                                            <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border border-gold/40 text-gold">
                                                {p.discount_type === "percent" ? `%${p.discount_value} indirim` : `−${fmtSAR(p.discount_value)}`}
                                            </span>
                                        </div>
                                        {p.source && <div className="text-sm text-bone/85">Kaynak: <span className="text-gold">{p.source}</span></div>}
                                        {p.description && <div className="text-xs text-muted_green mt-0.5">{p.description}</div>}
                                        {(p.valid_from || p.valid_until) && (
                                            <div className="text-[11px] text-muted_green mt-1">
                                                Geçerlilik: {p.valid_from ? fmtDate(p.valid_from) : "—"} → {p.valid_until ? fmtDate(p.valid_until) : "—"}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => toggleActive(p)} className="btn-outline-gold !text-xs" data-testid={`toggle-${p.code}`}>
                                            {p.active !== false ? "Pasife Al" : "Aktifleştir"}
                                        </button>
                                        <button onClick={() => openEdit(p)} className="text-muted_green hover:text-gold p-2" data-testid={`edit-${p.code}`}><Pencil className="w-4 h-4" /></button>
                                        <button onClick={() => remove(p)} className="text-rose-400 hover:text-rose-500 p-2" data-testid={`del-${p.code}`}><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-border_green text-sm">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted_green">Kullanım</div>
                                        <div className="font-serif text-xl text-gold">{p.used_count || 0}{Number(p.max_uses) > 0 ? ` / ${p.max_uses}` : ""}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted_green">Rezervasyon</div>
                                        <div className="font-serif text-xl">{p.reservations || 0}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted_green">Toplam İndirim</div>
                                        <div className="font-serif text-xl text-emerald-300">{fmtSAR(p.total_discount)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted_green">Getirdiği Gelir</div>
                                        <div className="font-serif text-xl text-gold">{fmtSAR(p.revenue)}</div>
                                    </div>
                                </div>

                                {(p.customers || []).length > 0 && (
                                    <button
                                        onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                                        className="mt-3 text-[11px] uppercase tracking-[0.15em] text-gold hover:underline inline-flex items-center gap-1"
                                        data-testid={`expand-${p.code}`}
                                    >
                                        <Users className="w-3.5 h-3.5" /> Bu koddan gelen müşteriler ({p.customers.length})
                                        {expanded === p.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                    </button>
                                )}
                            </div>
                            {expanded === p.id && (p.customers || []).length > 0 && (
                                <div className="border-t border-border_green overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-muted_green">
                                                <th className="px-5 py-2.5">Müşteri</th>
                                                <th className="px-5 py-2.5">Telefon</th>
                                                <th className="px-5 py-2.5">Otel</th>
                                                <th className="px-5 py-2.5">Tutar</th>
                                                <th className="px-5 py-2.5">Durum</th>
                                                <th className="px-5 py-2.5">Tarih</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border_green">
                                            {p.customers.map((c, i) => (
                                                <tr key={i}>
                                                    <td className="px-5 py-2.5">{c.name}</td>
                                                    <td className="px-5 py-2.5 font-mono text-xs">{c.phone}</td>
                                                    <td className="px-5 py-2.5 text-muted_green">{c.hotel}</td>
                                                    <td className="px-5 py-2.5 text-gold">{fmtSAR(c.total)}</td>
                                                    <td className="px-5 py-2.5 text-xs">{STATUS_TR[c.status] || c.status}</td>
                                                    <td className="px-5 py-2.5 text-xs text-muted_green">{fmtDate(c.created_at)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminPromoCodes;
